import { useTranslation } from 'react-i18next';
import { useAppStore } from '../../../shared/hooks/useAppStore';
import { DEFAULT_WEB_PAYMENT_MAX } from '../payment.constants';
import { buildAmountError } from '../payment.utils';

interface UseAmountValidationParams {
    min: number;
}

interface UseAmountValidationResult {
    min: number;
    max: number;
    validate: (amount: number) => string | null;
    limitToMaxAmount: (amount: number) => number;
}

/**
 * Single source of truth for amount limits on payment input screens.
 *
 * The backend's check.service.js / connect.service.js already resolves the
 * effective ceiling (vm.max_credit_app → company.max_credit_app → 500) into a
 * single `vm.max_credit_app` field. We trust that value and fall back to
 * DEFAULT_WEB_PAYMENT_MAX only as a safety net.
 */
export const useAmountValidation = ({
    min,
}: UseAmountValidationParams): UseAmountValidationResult => {
    const { t } = useTranslation();
    const vm = useAppStore((s) => s.vm);

    const max = vm.max_credit_app || DEFAULT_WEB_PAYMENT_MAX;
    const currencyCode = vm.currency_code;

    const validate = (amount: number): string | null => {
        if (amount < min) return buildAmountError(t, min, currencyCode, 'min');
        if (amount > max) return buildAmountError(t, max, currencyCode, 'max');
        return null;
    };

    const limitToMaxAmount = (amount: number): number => Math.min(amount, max);

    return { min, max, validate, limitToMaxAmount };
};
